import {
  EditRounded as FormIcon,
  DataObjectRounded as RawIcon,
} from "@mui/icons-material";
import {
  Alert,
  Grid2 as Grid,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import CodeEditor from "components/admin/CodeEditor";
import Button from "components/common/Button";
import CopyButton from "components/common/CopyButton";
import TextField from "components/common/TextField";
import { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { isSuperUser as selectIsSuperUser } from "store/selectors/user/userSelectors";

const RawConfigEditor = ({
  data,
  onSave,
  isSaving = false,
  saveError = false,
  pollForChanges,
  header,
  actions,
  children,
  superUserOnly = false,
}) => {
  const isSuperUser = useSelector(selectIsSuperUser);

  const [viewMode, setViewMode] = useState("form");
  const [rawJson, setRawJson] = useState(null);
  const [parseError, setParseError] = useState(null);
  const [userLogMessage, setUserLogMessage] = useState("");
  const baselineRef = useRef(null);
  const [isStale, setIsStale] = useState(false);

  const jsonValue = rawJson ?? JSON.stringify(data, null, 2);

  // Reset raw state when switching to form
  useEffect(() => {
    if (viewMode === "form") {
      setRawJson(null);
      setParseError(null);
      setUserLogMessage("");
    }
  }, [viewMode]);

  // Snapshot baseline when data first loads
  useEffect(() => {
    if (data && baselineRef.current === null) {
      baselineRef.current = JSON.stringify(data);
    }
  }, [data]);

  // Poll for external changes without updating editor data
  useEffect(() => {
    if (!pollForChanges || isStale || (superUserOnly && !isSuperUser)) return;
    const interval = setInterval(async () => {
      try {
        const latestData = await pollForChanges();
        if (
          baselineRef.current !== null &&
          JSON.stringify(latestData) !== baselineRef.current
        ) {
          setIsStale(true);
        }
      } catch {
        // Silently ignore poll failures
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [pollForChanges, isStale, isSuperUser, superUserOnly]);

  const hasChanges =
    rawJson !== null && rawJson !== JSON.stringify(data, null, 2);

  const handleSave = async () => {
    try {
      const parsed = JSON.parse(rawJson);
      await onSave(parsed, userLogMessage);
      setRawJson(null);
      setParseError(null);
      setUserLogMessage("");
      baselineRef.current = null;
      setIsStale(false);
    } catch (e) {
      setParseError(e.message);
    }
  };

  if (superUserOnly && !isSuperUser) {
    return (
      <Stack spacing={2}>
        <Stack
          direction={"row"}
          alignItems={"center"}
          justifyContent={"space-between"}
        >
          <Stack direction={"row"} alignItems={"center"} spacing={1}>
            {header}
          </Stack>
          {actions}
        </Stack>
        {children}
      </Stack>
    );
  }

  return (
    <Grid container spacing={2}>
      {isStale && (
        <Grid size={12}>
          <Alert
            severity={"warning"}
            action={
              <Button
                color={"inherit"}
                size={"small"}
                onClick={() => window.location.reload()}
              >
                {"Refresh"}
              </Button>
            }
          >
            {
              "This config has been updated by another user. Refresh to see the latest version."
            }
          </Alert>
        </Grid>
      )}
      <Grid size={12}>
        <Stack
          direction={"row"}
          alignItems={"center"}
          justifyContent={"space-between"}
        >
          <Stack direction={"row"} alignItems={"center"} spacing={1}>
            <ToggleButtonGroup
              value={viewMode}
              onChange={(_, v) => {
                if (v) setViewMode(v);
              }}
              exclusive
              size={"small"}
              color={"primary"}
            >
              <ToggleButton value={"form"}>
                <FormIcon fontSize={"small"} sx={{ mr: 0.5 }} />
                <span>{"Form"}</span>
              </ToggleButton>
              <ToggleButton value={"raw"}>
                <RawIcon fontSize={"small"} sx={{ mr: 0.5 }} />
                <span>{"Raw"}</span>
              </ToggleButton>
            </ToggleButtonGroup>
            {header}
            {viewMode === "raw" && hasChanges && (
              <Button
                variant={"contained"}
                disableElevation
                color={"primary"}
                onClick={() => {
                  setRawJson(null);
                  setParseError(null);
                }}
              >
                {"Undo edits"}
              </Button>
            )}
            {viewMode === "raw" && <CopyButton content={jsonValue} />}
          </Stack>
          {actions}
        </Stack>
      </Grid>
      {viewMode === "form" ? (
        <Grid size={12}>{children}</Grid>
      ) : (
        <>
          {parseError && (
            <Grid size={12}>
              <Alert severity={"error"}>{parseError}</Alert>
            </Grid>
          )}
          <Grid size={12}>
            <CodeEditor
              value={jsonValue}
              language={"json"}
              onChange={(value) => {
                setRawJson(value);
                try {
                  JSON.parse(value);
                  setParseError(null);
                } catch (e) {
                  setParseError(e.message);
                }
              }}
            />
          </Grid>
          {hasChanges && (
            <Grid container size={12} alignItems={"center"} spacing={2}>
              <Grid size={10}>
                <TextField
                  variant={"filled"}
                  label={"Reason for change"}
                  fullWidth
                  value={userLogMessage}
                  onChange={(e) => setUserLogMessage(e.target.value)}
                />
              </Grid>
              <Grid container size={2} justifyContent={"flex-end"}>
                <Button
                  variant={"contained"}
                  disableElevation
                  color={"primary"}
                  onClick={handleSave}
                  isLoading={isSaving}
                  isDisabled={isSaving || !!parseError || !userLogMessage}
                >
                  {"Save Changes"}
                </Button>
              </Grid>
            </Grid>
          )}
          {saveError && (
            <Grid size={12}>
              <Alert severity={"error"}>
                {"Failed to save. Please try again."}
              </Alert>
            </Grid>
          )}
        </>
      )}
    </Grid>
  );
};

export default RawConfigEditor;
